import { GetShareSettingsResponse } from '@orbit/api-types'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'

/**
 * Fetch the per-section public sharing flags for a trip (ADR 0003). The master
 * switch (`isVisibleToPublic`) plus the six section flags live on this
 * webapp-only `share-settings` resource, never on the shared Trip wire model.
 *
 * The GET endpoint is readable by any trip member, but the Sharing UI is
 * owner-only, so callers gate the query with `enabled` to avoid fetching for
 * members who never see the section.
 *
 * Exported as a factory so the query options can be unit-tested through a bare
 * `QueryObserver` without mounting React.
 */
export const shareSettingsQueryOptions = (
	tripId: string | undefined,
	enabled = true,
): UseQueryOptions<GetShareSettingsResponse> => ({
	queryKey: queryKeys.shareSettings(tripId),
	queryFn: async () => {
		if (!tripId) throw new Error('Trip ID is required')
		const response = await api.get<GetShareSettingsResponse>(`/trips/${tripId}/share-settings`)
		return response.data
	},
	enabled: enabled && !!tripId,
})

export const useShareSettings = (tripId: string | undefined, enabled = true) =>
	useQuery(shareSettingsQueryOptions(tripId, enabled))
